import { Button, Form, notification, Table } from 'antd';
import { ColumnsType, TablePaginationConfig, TableRowSelection } from 'antd/lib/table/interface';
import cx from 'classnames';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { deviceAdminApi } from '../../../../../api';
import {
	buildActionColumn,
	buildColumn,
	ColumnsProps,
	getDefaultTablePaginationConfig,
	getDefaultTableSelectionConfigPagination,
	getPageNumberSelectedItem,
	getSelectedRowKeysPagination,
	getUniqueValuesArray,
	handlePageChanged,
	handleResponse,
	invertSelectedRowKeys,
	ScrollType,
	timeZoneLockedValidation,
	useHandleOnChangeTableLogic,
} from '../../../../../Helper';
import { BaseColumns, PaginationSetting, ResponseObject, ResponseStatusCode, SelectOptions, SorterConfig } from '../../../../../model/CommonModel';
import {
	DeviceObjectType,
	GrandMasterTimeZones,
	OptionsButtonBuilder,
	SelectedRowKeyPagination,
	TimeZoneType,
	UseModel,
	VelocityConfigurationModel,
} from '../../../../../model/DeviceAdminModel';
import { useStoreDispatch, useStoreSelector } from '../../../../../store';
import { setCurrentModalTimeZone, setItemSearchedPagination, setTotalItemsPagination } from '../../../../../store/common/actions';
import { CommonState } from '../../../../../store/common/reducer';
import {
	clearVelocityConfigurationSelectionsAction,
	deleteTimeZoneGrand,
	setGrandTimeZonesBy,
	setGrandTimeZonesByData,
	setVelocityActionScopeKey,
	setVelocityConfigurationEditMode,
	setVelocityConfigurationFilterMode,
} from '../../../../../store/deviceControl/actions';
import { RootReducer } from '../../../../../store/rootReducer';
import { ModalConfirmationList, SearchColumn } from '../../../../common';
import { EditableCell } from '../../../../common/EditableCell/EditableCell';
import styles from './timeZonesGrandMaster.module.scss';

type Props = {
	data: GrandMasterTimeZones[];
	options: OptionsButtonBuilder<string>;
	actionScopeKey: string;
	shouldResetSearchColumn: boolean;
	selectedRowKeysPagination: SelectedRowKeyPagination[];
	currentPage: number;
	redirectPage: boolean;
	currentPageSize: number;
	sorterConfig: SorterConfig;
	selectedRowKeys: React.Key[];
	onShowAlert: (records: UseModel[], title: string, timeZoneType: TimeZoneType) => void;
	setShouldResetSearchColumn: (value: boolean) => void;
	setRedirectPage: (value: boolean) => void;
	setSelectedRowKeysPagination: (value: SelectedRowKeyPagination[]) => void;
	setCurrentPage: (value: number) => void;
	setIsItemOnSamePage: (value: boolean) => void;
	setCurrentPageSize: (value: number) => void;
	setSorterConfig: (value: SorterConfig) => void;
	setSelectedRowKeys: (value: React.Key[]) => void;
};

type GrandMasterTimeZoneColumns = {
	GrandMasterTimeZoneId: number;
	Name: string;
	MasterStandardTimeZones: string[];
	Action: React.ReactNode;
	InUse: boolean;
} & BaseColumns;

//Avoid creating object style inline, since increases reconciliations
const scroll: ScrollType = { x: 379, y: 370 };
const maxLength: number = 50;

const TimeZonesGrandMaster: React.FC<Props> = ({
	data,
	actionScopeKey,
	options,
	selectedRowKeysPagination,
	shouldResetSearchColumn,
	redirectPage,
	currentPage,
	currentPageSize,
	sorterConfig,
	selectedRowKeys,
	setSelectedRowKeys,
	onShowAlert,
	setShouldResetSearchColumn,
	setRedirectPage,
	setSelectedRowKeysPagination,
	setCurrentPage,
	setIsItemOnSamePage,
	setCurrentPageSize,
	setSorterConfig,
}) => {
	const dispatch = useStoreDispatch();
	const { isEditMode, isFilterMode }: VelocityConfigurationModel = useStoreSelector(
		(state: RootReducer): VelocityConfigurationModel => state.deviceControlReducer.velocityConfiguration
	);
	const { totalItemsPagination, itemSearchedPagination, allItemsPagination }: CommonState = useStoreSelector(
		(state: RootReducer): CommonState => state.commonReducer
	);
	const [tableData, setTableData] = useState<GrandMasterTimeZoneColumns[]>([]);
	const [grandMasterTimeZoneSelected, setGrandMasterTimeZoneSelected] = useState<GrandMasterTimeZones>();

	const handleOnChangeTableLogic = useHandleOnChangeTableLogic();

	const [editingKey, setEditingKey] = useState('');
	const [form] = Form.useForm();
	const isSearchPerformed = useRef<boolean>(false);
	const tablePaginationSetting: PaginationSetting = useMemo(
		() => ({
			PageNumber: currentPage,
			PageSize: currentPageSize,
			SortDirection: sorterConfig.direction,
			SortField: sorterConfig.field,
		}),
		[currentPage, currentPageSize, JSON.stringify(sorterConfig)]
	);

	useEffect(() => {
		const mapRes: GrandMasterTimeZoneColumns[] = createData();
		setTableData(mapRes);
		getPreviousSelectedRowKeys();
	}, [JSON.stringify(data)]);

	useEffect(() => {
		if (actionScopeKey !== '') {
			handleActionScope(actionScopeKey).then(res => dispatch(setVelocityActionScopeKey('')));
		}
	}, [actionScopeKey]);

	useEffect(() => {
		if (selectedRowKeys.length === 1 && redirectPage) {
			setRedirectPage(false);
			const grandMasterTimeZoneKeySelected = selectedRowKeys?.[0];
			getGrandMasterTimeZoneSelected(grandMasterTimeZoneKeySelected);
		}
	}, [JSON.stringify(tableData)]);

	useEffect(() => {
		if (selectedRowKeys.length === 1 && redirectPage) {
			const selectedGrandMasterTimezonePage = getPageNumberSelectedItem(
				allItemsPagination.map(x => x),
				grandMasterTimeZoneSelected.Id.toString(),
				pagination.pageSize
			);

			handlePageChanged(
				selectedGrandMasterTimezonePage,
				currentPage,
				() => setSelectedRowKeysPagination([{ Id: grandMasterTimeZoneSelected.Id.toString(), PaginationPage: selectedGrandMasterTimezonePage }]),
				setCurrentPage,
				setIsItemOnSamePage
			);

			const newConfig: PaginationSetting = {
				...tablePaginationSetting,
				PageNumber: selectedGrandMasterTimezonePage,
				SearchedValue: itemSearchedPagination,
			};

			dispatch(setGrandTimeZonesBy(newConfig));
		}
	}, [JSON.stringify(grandMasterTimeZoneSelected)]);

	useEffect(() => {
		return () => {
			clearSelection();
			clearPagination();
		};
	}, []);

	const clearPagination = () => {
		dispatch(setTotalItemsPagination(0));
		dispatch(setItemSearchedPagination(''));
	};

	const clearSelection = () => {
		dispatch(clearVelocityConfigurationSelectionsAction({ objectType: DeviceObjectType.TimeZones, subObjectType: 'grand' }));
		setSelectedRowKeys([]);
		setGrandMasterTimeZoneSelected(undefined);
	};

	const setSearchPerformed = (value: boolean) => {
		isSearchPerformed.current = value;
	};

	const getGrandMasterTimeZoneSelected = async (key: React.Key) => {
		const grandMasterTimeZoneId = Number(key);

		const grandMasterTimeZoneSelected = data.find(timeZone => timeZone.Id === grandMasterTimeZoneId);

		const grandMasterTimeZone = grandMasterTimeZoneSelected ? grandMasterTimeZoneSelected : await fetchGrandMasterTimeZone(grandMasterTimeZoneId);

		setGrandMasterTimeZoneSelected(grandMasterTimeZone);
	};

	const fetchGrandMasterTimeZone = async (grandMasterTimeZoneId: number): Promise<GrandMasterTimeZones> => {
		const { Entity: grandMasterTimeZone } = await deviceAdminApi.getGrandMasterTimeZone(grandMasterTimeZoneId);

		return grandMasterTimeZone;
	};

	const onDeleteTimezoneRequest = async (timeZone: GrandMasterTimeZones): Promise<void> => {
		const id = timeZone.Id;
		const response = await deviceAdminApi.deleteGrandMasterTimeZone(id);
		switch (response.ResponseStatusCode) {
			case ResponseStatusCode.FailedValidation:
				onShowAlert(response.InUseByList, _('TheFollowingDependenciesWhereFoundTimeZone').replace('%1', timeZone.Name), TimeZoneType.Grand);
				break;
			case ResponseStatusCode.Success:
				dispatch(deleteTimeZoneGrand(id));
				break;
			default:
				handleResponse(response);
				break;
		}
	};

	const handleOkConfirmation = async (grandMasterTimezonesSelected: GrandMasterTimeZones[]) => {
		for (const timeZone of grandMasterTimezonesSelected) {
			await onDeleteTimezoneRequest(timeZone);
		}
		clearSelection();
	};

	const handleChangePagination = (page: number, pageSize: number) => {
		const isSamePage: boolean = selectedRowKeysPagination[0]?.PaginationPage === page;
		setCurrentPage(page);
		setIsItemOnSamePage(isSamePage);
		if (pageSize !== currentPageSize) {
			clearSelection();
			setCurrentPageSize(pageSize);
		}
	};

	const disabled: boolean = editingKey !== '';
	const pagination: TablePaginationConfig = getDefaultTablePaginationConfig(
		isEditMode,
		handleChangePagination,
		currentPage,
		currentPageSize,
		totalItemsPagination,
		undefined,
		selectedRowKeys
	);
	const isPaginationItemSelected: boolean = selectedRowKeys.length !== 1;

	let columns: ColumnsProps<GrandMasterTimeZoneColumns>[] = [
		{
			...buildColumn(_('Name'), 'Name', '250px', 'start'),
			editable: true,
			sorter: !isEditMode,
			...SearchColumn({
				maxLength,
				dataIndex: undefined,
				reset: undefined,
				label: undefined,
				notVisible: isEditMode,
				resetSearch: shouldResetSearchColumn,
				setIsFilterMode: setVelocityConfigurationFilterMode,
				clearSelection: () => clearSelection(),
				handleResetSearch: () => setShouldResetSearchColumn(false),
				setSearchResults: (searchedValue: string) => {
					const newConfig: PaginationSetting = {
						...tablePaginationSetting,
						PageNumber: 1,
						SearchedValue: searchedValue,
					};
					dispatch(setGrandTimeZonesBy(newConfig));
				},
				setSearchedValue: (searchedValue: string) => dispatch(setItemSearchedPagination(searchedValue)),
				setSearchPerformed: (value: boolean) => setSearchPerformed(value),
			}),
			render: (text, row, index) => {
				return {
					children: <label id={`grandMasterTimeZoneName-${row.GrandMasterTimeZoneId}`}>{text}</label>,
					props: { className: row.InUse ? styles.inUse : styles.notReferenced },
				};
			},
		},
		{
			...buildColumn(_('MasterStandardTimeZones'), 'MasterStandardTimeZones', '250px', 'start'),
			render: (text, row) => {
				const isRowSelected = selectedRowKeys.findIndex(id => id === row.GrandMasterTimeZoneId.toString()) !== -1;
				let children: ReactNode = undefined;
				let className = undefined;

				row.MasterStandardTimeZones.length > 0
					? ((children = (
							<div className={cx(styles.gridGMTimezone, { [styles.rowSelected]: isRowSelected || (isEditMode && isRowSelected) })}>
								{row.MasterStandardTimeZones.map((timezone, index) => (
									<React.Fragment key={`${row.GrandMasterTimeZoneId}-${index}`}>
										<div>{timezone}</div>
									</React.Fragment>
								))}
							</div>
					  )),
					  (className = styles.gridGMTimezoneContainer))
					: ((children = (
							<div
								className={cx(styles.extraColumnTableContainer, {
									[styles.extraColumnTableSelected]: isRowSelected || (isEditMode && isRowSelected),
								})}></div>
					  )),
					  (className = cx(styles.extraColumnTable, { [styles.extraColumnTableSelected]: isRowSelected || (isEditMode && isRowSelected) })));
				return {
					children,
					props: { className },
				};
			},
		},
	];
	if (isEditMode) {
		columns.unshift({
			...buildActionColumn(disabled),
			render: (value, row, index) => {
				return {
					children: <label id={`grandMasterTimeZoneActionContainer-${row.GrandMasterTimeZoneId}`}>{value}</label>,
					props: { className: row.InUse ? styles.inUse : styles.notReferenced },
				};
			},
		});
	}

	const onChangeSelection = (key: GrandMasterTimeZoneColumns) => {
		const grandMasterTimezoneIdSelected = key.key;
		if (selectedRowKeys.findIndex(timezoneId => timezoneId === grandMasterTimezoneIdSelected) === -1) {
			const cloneStateProps: GrandMasterTimeZones[] = data.map(x =>
				x.Id?.toString() === grandMasterTimezoneIdSelected ? { ...x, checked: true } : { ...x, checked: false }
			);
			dispatch(setGrandTimeZonesByData(cloneStateProps));

			const selectedKeysPagination: SelectedRowKeyPagination = {
				Id: grandMasterTimezoneIdSelected,
				PaginationPage: currentPage,
			};
			setIsItemOnSamePage(true);
			setSelectedRowKeysPagination([{ ...selectedKeysPagination }]);
			setSelectedRowKeys([grandMasterTimezoneIdSelected]);
		}
	};

	const isEditing = (record: GrandMasterTimeZoneColumns) => record.GrandMasterTimeZoneId.toString() === editingKey;
	const contextMenuOptions: SelectOptions<string>[] = [...options?.options];

	const mergedColumns = columns.map(col =>
		!col.editable
			? {
					...col,
					onCell: (record: GrandMasterTimeZoneColumns) => ({
						record,
						dataIndex: col.dataIndex,
						title: col.title,
						maxLength,
						options: contextMenuOptions,
						isTableInEditMode: isEditMode,
						onChangeSelection,
						onClickOption: handleActionScope,
					}),
			  }
			: {
					...col,
					onCell: (record: GrandMasterTimeZoneColumns) => ({
						record,
						dataIndex: col.dataIndex,
						title: col.title,
						maxLength,
						options: contextMenuOptions,
						isTableInEditMode: isEditMode,
						onChangeSelection,
						onClickOption: handleActionScope,
						inputType: 'text',
						editing: isEditing(record),
					}),
			  }
	);

	const getGrandMasterTimeZonesSelected = async (): Promise<GrandMasterTimeZones[]> => {
		const { Entity: grandMasterTimeZones } = await deviceAdminApi.retrieveGrandMasterTimeZones();

		const grandMasterTimeZonesSelected = grandMasterTimeZones.filter(
			stdTimeZone => selectedRowKeys.findIndex(k => k === stdTimeZone.Id?.toString()) !== -1
		);

		return grandMasterTimeZonesSelected;
	};

	const handleActionScope = async (key: string) => {
		setRedirectPage(false);

		if (key === 'delete') {
			const grandMasterTimeZonesSelected = await getGrandMasterTimeZonesSelected();
			ModalConfirmationList({
				type: _('GrandMasterTimeZone'),
				data: grandMasterTimeZonesSelected,
				onConfirm: () => handleOkConfirmation(grandMasterTimeZonesSelected),
			});

			return;
		}

		const grandMasterTimeZoneIdSelected = Number(selectedRowKeys?.[0]);
		const grandMasterTimeZoneSelected = data.find(timeZone => timeZone.Id === grandMasterTimeZoneIdSelected);
		const timeZone: GrandMasterTimeZones = grandMasterTimeZoneSelected
			? grandMasterTimeZoneSelected
			: await fetchGrandMasterTimeZone(grandMasterTimeZoneIdSelected);
		const lockComponentCondition: boolean = key === 'edit';
		const isLocked: boolean = await timeZoneLockedValidation(TimeZoneType.Grand, lockComponentCondition);

		if (isLocked) {
			return;
		}

		switch (key) {
			case 'rename':
				const cloneState: GrandMasterTimeZones[] = changeStateProps(timeZone.Id, { editable: true });
				dispatch(setVelocityConfigurationEditMode(true));
				const findKey: GrandMasterTimeZoneColumns = tableData.find(x => x.GrandMasterTimeZoneId === timeZone.Id);
				edit(findKey);
				dispatch(setGrandTimeZonesByData(cloneState));
				break;

			case 'edit':
				dispatch(setCurrentModalTimeZone({ entity: timeZone, timeZoneType: TimeZoneType.Grand }));
				break;
		}
	};

	const changeStateProps = (deviceId: number, grandMasterTimeZone: Partial<GrandMasterTimeZones>): GrandMasterTimeZones[] => {
		const cloneState: GrandMasterTimeZones[] = [...data];
		const index = cloneState.findIndex(x => x.Id === deviceId);
		if (~index) {
			const item = cloneState[index];
			cloneState.splice(index, 1, { ...item, ...grandMasterTimeZone });
		}

		return cloneState;
	};

	const edit = (record: GrandMasterTimeZoneColumns) => {
		form.setFieldsValue({
			Name: '',
			...record,
		});
		setEditingKey(record.GrandMasterTimeZoneId.toString());
	};

	const handleOnSaveEditRow = async (deviceId: number) => {
		const isLocked: boolean = await timeZoneLockedValidation(TimeZoneType.Grand, false);
		if (isLocked) {
			setEditingKey('');
			const cloneStateProps: GrandMasterTimeZones[] = changeStateProps(deviceId, { editable: false });
			dispatch(setGrandTimeZonesByData(cloneStateProps));
			dispatch(setVelocityConfigurationEditMode(false));
			return;
		}

		const name: string = form.getFieldValue('Name');
		const nameError: ResponseObject = await deviceAdminApi.renameDevice(DeviceObjectType.TimeZones, name.trim(), deviceId, TimeZoneType.Grand);
		const newConfig: PaginationSetting = {
			...tablePaginationSetting,
			SearchedValue: itemSearchedPagination,
		};
		if (nameError.ResponseStatusCode !== ResponseStatusCode.Success && nameError.ErrorMessage) {
			form.setFields([
				{
					name: 'Name',
					errors: [nameError.ErrorMessage],
				},
			]);
			return;
		} else if (nameError.ResponseStatusCode === ResponseStatusCode.PermissionError) {
			notification['error']({
				message: nameError.ResponseErrorDescription,
			});
			setEditingKey('');
			dispatch(setVelocityConfigurationEditMode(false));
			dispatch(setGrandTimeZonesBy(newConfig));
			return;
		}

		setEditingKey('');
		setRedirectPage(true);
		dispatch(setGrandTimeZonesBy(newConfig));
		dispatch(setVelocityConfigurationEditMode(false));
		if (!isPaginationItemSelected && isFilterMode) {
			setShouldResetSearchColumn(true);
		}
	};

	const handleOnCancelEditRow = (deviceId: number) => {
		const cloneStateProps: GrandMasterTimeZones[] = changeStateProps(deviceId, { editable: false });
		dispatch(setGrandTimeZonesByData(cloneStateProps));
		dispatch(setVelocityConfigurationEditMode(false));
		setEditingKey('');
	};

	const getSelectedRowKeysForSelectAll = (newSelectedRowKeys: React.Key[]): React.Key[] => {
		return getUniqueValuesArray<React.Key>(newSelectedRowKeys, allItemsPagination);
	};

	const handleChange = (newSelectedRowKeys: React.Key[]) => {
		const cloneStateProps: GrandMasterTimeZones[] = data.map(x => ({ ...x, checked: newSelectedRowKeys.findIndex(r => r === x.Id?.toString()) !== -1 }));
		const selectedKeysPagination: SelectedRowKeyPagination[] = getSelectedRowKeysPagination(
			newSelectedRowKeys,
			selectedRowKeysPagination,
			currentPage,
			setIsItemOnSamePage
		);
		dispatch(setGrandTimeZonesByData(cloneStateProps));
		setSelectedRowKeysPagination(selectedKeysPagination);
		setSelectedRowKeys(newSelectedRowKeys);
	};

	const handleSelectAll = () => {
		const newSelectedRowKeys = getSelectedRowKeysForSelectAll(selectedRowKeys);

		const cloneStateProps: GrandMasterTimeZones[] = data.map(x => ({ ...x, checked: newSelectedRowKeys.findIndex(r => r === x.Id?.toString()) !== -1 }));

		const selectedKeysPagination: SelectedRowKeyPagination[] = getSelectedRowKeysPagination(
			newSelectedRowKeys,
			selectedRowKeysPagination,
			currentPage,
			setIsItemOnSamePage
		);
		dispatch(setGrandTimeZonesByData(cloneStateProps));
		setSelectedRowKeysPagination(selectedKeysPagination);
		setSelectedRowKeys(newSelectedRowKeys);
	};

	const handleSelectInvert = () => {
		const dataKeys = data.map<React.Key>(x => x.Id?.toString());

		const newSelectedRowKeys = invertSelectedRowKeys(dataKeys, selectedRowKeys);

		const cloneStateProps: GrandMasterTimeZones[] = data.map(x => ({
			...x,
			checked: newSelectedRowKeys.findIndex(key => key === x.Id?.toString()) !== -1,
		}));

		const selectedKeysPagination: SelectedRowKeyPagination[] = getSelectedRowKeysPagination(
			newSelectedRowKeys,
			selectedRowKeysPagination,
			currentPage,
			setIsItemOnSamePage
		);
		dispatch(setGrandTimeZonesByData(cloneStateProps));
		setSelectedRowKeysPagination(selectedKeysPagination);
		setSelectedRowKeys(newSelectedRowKeys);
	};

	const createActionElement = (grandMasterTimeZone: GrandMasterTimeZones): React.ReactNode => {
		let content: React.ReactNode = undefined;
		if (grandMasterTimeZone.editable) {
			const id = grandMasterTimeZone.Id;

			content = (
				<>
					<Button id='renameGrandMasterTimeZoneSaveButton' key='save' type='primary' onClick={() => handleOnSaveEditRow(id)}>
						{_('Save')}
					</Button>
					<Button id='renameGrandMasterTimeZoneCancelButton' key='cancel' onClick={() => handleOnCancelEditRow(id)}>
						{_('Cancel')}
					</Button>
				</>
			);
		}

		return <div className={styles.actions}>{content}</div>;
	};

	const createData = (): GrandMasterTimeZoneColumns[] => {
		if (data.length > 0) {
			let result: GrandMasterTimeZoneColumns[] = [];
			data.forEach((grandMasterTimeZone: GrandMasterTimeZones) => {
				let grandMasterColumn: GrandMasterTimeZoneColumns[];
				if (grandMasterTimeZone.MasterAndStandardTimeZones.length === 0) {
					grandMasterColumn = [
						{
							GrandMasterTimeZoneId: grandMasterTimeZone.Id,
							key: grandMasterTimeZone.Id?.toString(),
							Action: createActionElement(grandMasterTimeZone),
							Name: grandMasterTimeZone.Name,
							InUse: grandMasterTimeZone.InUse,
							MasterStandardTimeZones: [],
						},
					];
				} else {
					grandMasterColumn = [
						{
							GrandMasterTimeZoneId: grandMasterTimeZone.Id,
							key: grandMasterTimeZone.Id?.toString(),
							Action: createActionElement(grandMasterTimeZone),
							Name: grandMasterTimeZone.Name,
							InUse: grandMasterTimeZone.InUse,
							MasterStandardTimeZones: grandMasterTimeZone.MasterAndStandardTimeZones,
						},
					];
				}

				result.push(...grandMasterColumn);
			});

			return result;
		}

		return [];
	};

	const getPreviousSelectedRowKeys = () => {
		const previousSelectedRowKeys = data.reduce<React.Key[]>((result, item) => {
			if (item.checked) {
				result.push(item.Id.toString());
			}
			return result;
		}, []);

		setSelectedRowKeys(getUniqueValuesArray(previousSelectedRowKeys, selectedRowKeys));
	};

	const rowSelection: TableRowSelection<GrandMasterTimeZoneColumns> = {
		preserveSelectedRowKeys: true,
		type: 'checkbox',
		selections: getDefaultTableSelectionConfigPagination(disabled, handleSelectAll, handleSelectInvert),
		selectedRowKeys,
		getCheckboxProps: record => ({
			id: `grandMasterTimeZonesTableCheckbox-${record.key}`,
			disabled,
		}),
		onChange: handleChange,
	};

	const handleOnChangeTable = (pagination, filters: { Name?: string[] }, sorter) => {
		const { current, pageSize, shouldUpdateSearchResults, shouldUpdateTableResults, sortOrder } = handleOnChangeTableLogic({
			clearSelection,
			filters,
			handleChangePagination,
			isSearchPerformed,
			pagination,
			shouldResetSearchColumn,
			sorter,
			tablePaginationSetting,
		});

		if (shouldUpdateTableResults || shouldUpdateSearchResults) {
			dispatch(
				setGrandTimeZonesBy({
					...tablePaginationSetting,
					PageNumber: current,
					PageSize: pageSize,
					SortDirection: sortOrder,
					SearchedValue: itemSearchedPagination,
				})
			);
		}
	};

	return (
		<div id='grandMasterTimeZonesTable' className={styles.container}>
			<Form form={form} component={false}>
				<Table
					id='grandMasterTimeZonesTable'
					columns={mergedColumns as ColumnsType}
					components={{
						body: {
							cell: EditableCell,
						},
					}}
					dataSource={tableData}
					pagination={pagination}
					scroll={scroll}
					size='small'
					rowClassName={(record: GrandMasterTimeZoneColumns, index) => {
						return record.InUse ? styles.inUse : styles.notReferenced;
					}}
					className={styles.grandMasterTimeZonesTable}
					rowSelection={rowSelection}
					onChange={handleOnChangeTable}
					onHeaderRow={() => ({ id: 'grandMasterTimeZonesTableHeader' })}
					getPopupContainer={() => document.getElementById('grandMasterTimeZonesTableHeader')}
				/>
			</Form>
		</div>
	);
};

export { TimeZonesGrandMaster };
